import type { ComponentType } from 'react';
import { choiceCard } from '../../shared/registry/choice-card.ts';
import { infoCard } from '../../shared/registry/info-card.ts';
import { speakingTask } from '../../shared/registry/speaking-task.ts';
import type { CanvasItem, TaskView } from '../session/conversation.ts';
import { ChoiceCard } from './ChoiceCard.tsx';
import { InfoCard } from './InfoCard.tsx';
import { SpeakingTaskCard } from './SpeakingTaskCard.tsx';

export type RendererProps = {
  params: Record<string, unknown>;
  picked?: string;
  onPick?: (option: { id: string; label: string }) => void;
  task?: CanvasItem['task'];
  live?: TaskView | null;
  onDone?: () => void;
  onSkip?: () => void;
};

// One renderer per client-executed spec. registry.client.test.ts fails if a client spec has no renderer here.
// Params arrive already validated against the spec's zod schema (see Conversation.onToolCalls).
export const RENDERERS: Record<string, ComponentType<RendererProps>> = {
  [infoCard.name]: ({ params }) => <InfoCard {...(params as Parameters<typeof InfoCard>[0])} />,
  [choiceCard.name]: ({ params, picked, onPick }) => (
    <ChoiceCard {...(params as Parameters<typeof ChoiceCard>[0])} picked={picked} onPick={onPick} />
  ),
  [speakingTask.name]: ({ params, task, live, onDone, onSkip }) => (
    <SpeakingTaskCard
      {...(params as Parameters<typeof SpeakingTaskCard>[0])}
      task={task}
      live={live}
      onDone={onDone}
      onSkip={onSkip}
    />
  ),
};

export function RegistryItem({ tool, ...rest }: RendererProps & { tool: string }) {
  const Renderer = RENDERERS[tool];
  if (!Renderer) return <div className="card text-sm text-muted">Unknown component: {tool}</div>;
  return <Renderer {...rest} />;
}
